import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';

const Withdrawals = () => {
  const navigate = useNavigate();
  const [spinner, setSpinner] = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);
  const [balance, setBalance] = useState(0);
  const [balance2, setBalance2] = useState(0);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedWithdrawalId, setSelectedWithdrawalId] = useState(null);

  // Check if user is logged in
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('LoggedIn');
    if (!isLoggedIn || isLoggedIn !== '1') {
      toast.error('Please login first');
      navigate('/login');
      return;
    }
    
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const userid = localStorage.getItem('userid');
      if (!userid) {
        toast.error('User ID not found');
        navigate('/login');
        return;
      }

      // Fetch balance
      const balanceUrl = `https://octafxx.in/Rest/home_wallet_api/${userid}`;
      const balanceResponse = await fetch(balanceUrl);
      const balanceData = await balanceResponse.json();
      setBalance(balanceData.bal || 0);
      setBalance2(balanceData.bal2 || 0);

      // Fetch withdrawals history
      await fetchWithdrawals(userid);
      
      setSpinner(false);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
      setSpinner(false);
    }
  };

  const fetchWithdrawals = async (userid) => {
    try {
      const url = `https://octafxx.in/Rest/withdrawals_api/${userid}`;
      const response = await fetch(url);
      const data = await response.json();
      setWithdrawals(data);
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      toast.error('Failed to load withdrawal history');
    }
  };

  const handleWithdrawSubmit = async (e) => {
    e?.preventDefault();
    
    if (!withdrawAmount) {
      toast.error('Please enter withdrawal amount');
      return;
    }

    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount < 1) {
      toast.error('Please enter a valid amount (minimum 1)');
      return;
    }

    

    setSpinner(true);
    
    try {
      const userid = localStorage.getItem('userid');
      
      const formData = new FormData();
      formData.append('withdrawAmt', amount);

      const response = await fetch(`https://octafxx.in/Rest/web_withdraw_submit_api/${userid}`, {
        method: 'POST',
        body: formData
      });

      const responseData = await response.json();
      
      if (responseData.status==0) {
        toast.success(responseData.msg || 'Withdrawal request submitted successfully');
        setWithdrawAmount('');
        
        // Refresh data
        await loadData();
      } else {
        toast.error(responseData.msg || 'Failed to submit withdrawal request');
      }
    } catch (error) {
      console.error('Withdrawal submission error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setSpinner(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!selectedWithdrawalId) return;

    setSpinner(true);
    
    try {
      const url = `https://octafxx.in/Rest/cancel_withdrawal_api/${selectedWithdrawalId}`;
      const response = await fetch(url);
      const responseData = await response.json();
      
      if (responseData.status === 0) {
        toast.success('Withdrawal request cancelled successfully');
        setDeleteModal(false);
        setSelectedWithdrawalId(null);
        
        // Refresh withdrawals list
        const userid = localStorage.getItem('userid');
        await fetchWithdrawals(userid);
      } else {
        toast.error(responseData.msg || 'Failed to cancel withdrawal');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setSpinner(false);
    }
  };

  const confirmDelete = (id) => {
    setSelectedWithdrawalId(id);
    setDeleteModal(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'processing': return 'info';
      case 'rejected':
      case 'reject': return 'danger';
      case 'cancelled': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'bi-clock';
      case 'approved': return 'bi-check-circle';
      case 'processing': return 'bi-arrow-repeat';
      case 'rejected':
      case 'reject': return 'bi-x-circle';
      case 'cancelled': return 'bi-slash-circle';
      default: return 'bi-question-circle';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (spinner && withdrawals.length === 0) {
    return (
      <div className="container-fluid bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading withdrawal information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid bg-light min-vh-100">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Confirm Cancellation
                </h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => {
                    setDeleteModal(false);
                    setSelectedWithdrawalId(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to cancel this withdrawal request?</p>
                <p className="text-muted small">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setDeleteModal(false);
                    setSelectedWithdrawalId(null);
                  }}
                >
                  No, Keep It
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteRequest}
                  disabled={spinner}
                >
                  {spinner ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash me-1"></i>
                      Yes, Cancel Withdrawal
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary shadow">
        <div className="container-fluid">
          <div className="d-flex align-items-center">
            <button 
              className="btn btn-link text-white me-2"
              onClick={() => navigate(-1)}
            >
              <i className="bi bi-arrow-left fs-4"></i>
            </button>
            <h1 className="navbar-brand mb-0 h1 fs-4">Withdraw Funds</h1>
          </div>
          <div>
            <button 
              className="btn btn-outline-light btn-sm"
              onClick={() => navigate('/deposit')}
            >
              <i className="bi bi-arrow-down-circle me-1"></i>
              Deposit
            </button>
          </div>
        </div>
      </nav>

      <div className="container py-4">
        {/* Balance and Withdrawal Form Card */}
        <div className="card bg-primary text-white shadow-lg mb-4 border-0">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-lg-4 mb-3 mb-lg-0">
                <div>
                  <small className="opacity-75">Available Balance</small>
                  <h2 className="fw-bold mb-0">${balance}</h2>
                   <small>₹{balance2}</small>
                </div>
              </div>
              
              <div className="col-lg-8">
                <form onSubmit={handleWithdrawSubmit}>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-6">
                      <label htmlFor="withdrawAmount" className="form-label text-white">
                        Withdrawal Amount(₹)
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white">
                          <i className="bi bi-currency-rupee"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-lg"
                          id="withdrawAmount"
                          placeholder="Enter amount to withdraw"
                          value={withdrawAmount}
                          onChange={(e) => setWithdrawAmount(e.target.value)}
                          min="1"
                          max={balance2}
                          step="0.01"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <button 
                        type="submit"
                        className="btn btn-light btn-lg w-100"
                        disabled={spinner}
                      >
                        {spinner ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Processing...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-arrow-up-circle me-2"></i>
                            Withdraw Now
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="row mt-3">
                    <div className="col-12">
                      <div className="alert alert-warning bg-warning bg-opacity-25 border-0 mb-0">
                        <i className="bi bi-info-circle me-2 text-white"></i> Funds are typically processed within 24-48 hours.
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Withdrawal Options */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body">
            <h6 className="card-title fw-bold mb-3">
              <i className="bi bi-lightning me-2"></i>
              Quick Withdrawal Options
            </h6>
            <div className="row g-2">
              {[500, 1000, 2000, 5000, 10000].map((amount) => (
                <div key={amount} className="col">
                  <button
                    type="button"
                    className="btn btn-outline-primary w-100"
                    onClick={() => {
                      if (amount <= balance) {
                        setWithdrawAmount(amount.toString());
                      } else {
                        toast.error(`Insufficient balance for ${amount} withdrawal`);
                      }
                    }}
                    disabled={amount > balance}
                  >
                    {amount.toLocaleString()}
                  </button>
                </div>
              ))}
              <div className="col">
                <button
                  type="button"
                  className="btn btn-outline-success w-100"
                  onClick={() => {
                    if (balance >= 100) {
                      setWithdrawAmount(balance.toString());
                    } else {
                      toast.error('Minimum withdrawal amount is 100');
                    }
                  }}
                  disabled={balance < 100}
                >
                  <i className="bi bi-wallet2 me-1"></i>
                  Withdraw All
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Withdrawal History */}
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-0">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0 fw-bold">
                <i className="bi bi-clock-history me-2"></i>
                Withdrawal History
              </h5>
              <button 
                className="btn btn-outline-primary btn-sm"
                onClick={() => loadData()}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </button>
            </div>
          </div>
          
          <div className="card-body">
            {withdrawals.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-wallet display-1 text-muted mb-3"></i>
                <h5 className="text-muted">No Withdrawal History</h5>
                <p className="text-muted">Your withdrawal requests will appear here</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Date & Time</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Reference</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {withdrawals.map((withdrawal, index) => (
                        <tr key={index}>
                          <td>
                            <div className="small text-muted">
                              {withdrawal.req_date}
                            </div>
                          </td>
                          <td className="fw-bold">
                            {parseFloat(withdrawal.wamt || 0)}
                          </td>
                          <td>
                            <span className={`badge bg-${getStatusColor(withdrawal.req_status)}`}>
                              <i className={`bi ${getStatusIcon(withdrawal.req_status)} me-1`}></i>
                              {withdrawal.req_status || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {withdrawal.wid ? `WID-${withdrawal.wid}` : 'N/A'}
                            </small>
                          </td>
                          <td>
                            {withdrawal.req_status?.toLowerCase() === 'pending' && (
                              <button 
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => confirmDelete(withdrawal.wid)}
                                title="Cancel Withdrawal"
                              >
                                Cancel <i className="bi bi-x-circle"></i>
                              </button>
                            )}
                            {withdrawal.req_status?.toLowerCase() === 'approved' && (
                              <button 
                                className="btn btn-sm btn-outline-success"
                                title="Track Payment"
                              >
                                {withdrawal.req_status} <i className="bi bi-truck"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted small">
                    Showing {withdrawals.length} withdrawal requests
                  </div>
                  <div className="small">
                    <span className="me-3">
                      <span className="badge bg-warning me-1">Pending</span>
                      {withdrawals.filter(w => w.req_status?.toLowerCase() === 'pending').length}
                    </span>
                    <span className="me-3">
                      <span className="badge bg-success me-1">Approved</span>
                      {withdrawals.filter(w => w.req_status?.toLowerCase() === 'approved').length}
                    </span>
                    <span>
                      <span className="badge bg-danger me-1">Rejected</span>
                      {withdrawals.filter(w => w.req_status?.toLowerCase() === 'reject').length}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Information Section */}
        <div className="row mt-4">
          <div className="col-md-6">
            <div className="card bg-light border-0">
              <div className="card-body">
                <h6 className="card-title fw-bold">
                  <i className="bi bi-info-circle text-primary me-2"></i>
                  Withdrawal Instructions
                </h6>
                <ul className="list-unstyled mb-0">
                  <li className="mb-2">
                    <i className="bi bi-check-circle text-success me-2"></i>
                    Minimum withdrawal amount is 100
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle text-success me-2"></i>
                    Withdrawals are processed within 24-48 hours
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle text-success me-2"></i>
                    Use the same bank account registered with us
                  </li>
                  <li>
                    <i className="bi bi-check-circle text-success me-2"></i>
                    You can cancel pending withdrawal requests
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="card bg-light border-0">
              <div className="card-body">
                <h6 className="card-title fw-bold">
                  <i className="bi bi-shield-check text-primary me-2"></i>
                  Important Notes
                </h6>
                <ul className="list-unstyled mb-0">
                  <li className="mb-2">
                    <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                    Processing fees may apply for certain withdrawal methods
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                    Withdrawals may be delayed during weekends and holidays
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                    Ensure your KYC is complete for faster processing
                  </li>
                  <li>
                    <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                    Contact support for any withdrawal-related issues
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bank Transfer Information */}
        <div className="card bg-light border-0 mt-4">
          <div className="card-body">
            <h6 className="card-title fw-bold">
              <i className="bi bi-bank text-primary me-2"></i>
              Bank Transfer Information
            </h6>
            <p className="mb-0">
              Withdrawals are processed via NEFT/RTGS/IMPS to your registered bank account. 
              Please ensure your bank details are up-to-date in your profile. 
              For UPI withdrawals, ensure your UPI ID is verified.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-dark text-white py-3 mt-4">
        <div className="container">
          <div className="row">
            <div className="col-md-6">
              <small className="opacity-75">© {new Date().getFullYear()} Secure Withdrawal System</small>
            </div>
            <div className="col-md-6 text-md-end">
              <small className="opacity-75">
                <i className="bi bi-headset me-1"></i>
                Support: support@example.com | 
                <i className="bi bi-telephone ms-2 me-1"></i>
                1800-123-4567
              </small>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Withdrawals;